import * as React from 'react';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useUser } from 'hooks/use-user';
import { authClient } from 'lib/auth/client';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';

const schema = zod.object({
  email: zod.string().min(1, { message: 'Email is required' }).email(),
});

type Values = zod.infer<typeof schema>;

const defaultValues = { email: '' } satisfies Values;

const parseError = (error: string) => {
  if (error.includes('auth/admin-restricted-operation')) {
    return 'User is not authorized to access the platform.';
  }

  if (error.includes('auth/invalid-action-code')) {
    return 'The link is invalid or expired. Please try to sign in again.';
  }

  return error;
};

export function SignInWithEmailLinkForm(): React.JSX.Element {
  const { checkSession, pendingEmailConfirmation, error } = useUser();

  const [isPending, setIsPending] = React.useState<boolean>(false);
  const [isLinkedSent, setIsLinkedSent] = React.useState<boolean>(false);

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  useEffect(() => {
    if (error) {
      setError('root', { type: 'server', message: parseError(error) });
    }
  }, [error, setError]);

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      setIsPending(true);

      let result;
      if (pendingEmailConfirmation) {
        result = await authClient.signInWithEmailLink(values);
      } else {
        result = await authClient.signInWithLinkToEmail(values);
      }

      if (result.error) {
        reset();
        setError('root', { type: 'server', message: parseError(result.error) });
        setIsPending(false);
        await checkSession?.();
        return;
      }

      // Refresh the auth state
      await checkSession?.();

      setIsLinkedSent(true);
      setIsPending(false);
    },
    [checkSession, setError, pendingEmailConfirmation]
  );

  return (
    <Stack spacing={4}>
      {errors.root ? <Alert color="error">{errors.root.message}</Alert> : null}
      <Stack spacing={1}>
        {!pendingEmailConfirmation && <Typography variant="h4">Sign in</Typography>}
        {pendingEmailConfirmation && <Typography variant="h4">Confirm your email</Typography>}
      </Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <FormControl error={Boolean(errors.email)}>
                <InputLabel>Email address</InputLabel>
                <OutlinedInput {...field} label="Email address" type="email" />
                {errors.email ? <FormHelperText>{errors.email.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
          {isLinkedSent ? <Alert color="info">Please check your inbox to find the sign-in link.</Alert> : null}
          {!isLinkedSent && (
            <Button disabled={isPending} type="submit" variant="contained">
              Sign in
            </Button>
          )}
          {isLinkedSent && (
            <Button disabled={isPending} type="submit" variant="contained">
              Re-send link
            </Button>
          )}
        </Stack>
      </form>
    </Stack>
  );
}
